import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';

const C = {
  purple:"#691FB1", purpleLight:"#EDE7F6",
  red:"#FF357B", redLight:"#FFF0F5",
  amber:"#F39C12", amberLight:"#FEF9E7",
  green:"#27AE60", greenLight:"#EAFAF1",
  blue:"#57B1DD", blueLight:"#E5F7FF",
  bg:"#F7F5FC", white:"#FFFFFF",
  text:"#1A1A2E", textMid:"#4A4A6A", textLight:"#8888AA",
  border:"#E4DEFA",
};

const TAGS = {
  Onboarding:    { color:"#57B1DD", bg:"#E5F7FF" },
  RH:            { color:"#691FB1", bg:"#EDE7F6" },
  Técnico:       { color:"#1A3A6A", bg:"#E0E8F5" },
  Comunicação:   { color:"#27AE60", bg:"#EAFAF1" },
};

const Tag = ({ type }) => {
  const t = TAGS[type] || TAGS.Onboarding;
  return <span style={{ background:t.bg, color:t.color, borderRadius:4, padding:'1px 7px', fontSize:10, fontWeight:700, flexShrink:0 }}>{type}</span>;
};

const CHANNELS = ["Email corporativo","Intranet","Microsoft Teams","Slack","WhatsApp interno","Newsletter interna","Outro"];
const PACKAGES = ["WOW Care","WOW Ilimitado","WOW Flex","WOW Limitado Global","WOW Limitado por Utilizador"];
const VALENCIAS = ["Psicologia","Psiquiatria","Coaching","Nutrição","Sono","Financeiro","Jurídico/Social"];
const METRICS   = ["Adesão","Risco psicossocial","Utilização","NPS","Absentismo","Outro"];
const FORMATS   = ["Presencial","Videoconferência","Híbrido"];
const TONES     = ["Formal","Próximo","Institucional"];
const FREQUENCIES = ["Mensal","Bimestral","Trimestral","Semestral"];

function CheckItem({ label, tag, note, value, onChange }) {
  return (
    <div style={{ display:'flex', alignItems:'flex-start', gap:10, padding:'8px 0', borderBottom:`1px solid ${C.border}` }}>
      <input type="checkbox" checked={!!value} onChange={e=>onChange(e.target.checked)}
        style={{ marginTop:3, cursor:'pointer', accentColor:C.purple, flexShrink:0, width:16, height:16 }}/>
      <div style={{ flex:1 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
          <span style={{ fontSize:13, color:value?C.purple:C.text, fontWeight:value?600:400 }}>{label}</span>
          <Tag type={tag}/>
        </div>
        {note&&<div style={{ fontSize:11, color:C.textLight, marginTop:2, fontStyle:'italic' }}>ℹ {note}</div>}
      </div>
    </div>
  );
}

function Field({ label, tag, note, children }) {
  return (
    <div style={{ padding:'10px 0', borderBottom:`1px solid ${C.border}` }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6, flexWrap:'wrap' }}>
        <label style={{ fontSize:13, fontWeight:600, color:C.text }}>{label}</label>
        {tag&&<Tag type={tag}/>}
      </div>
      {note&&<div style={{ fontSize:11, color:C.textLight, marginBottom:6, fontStyle:'italic' }}>ℹ {note}</div>}
      {children}
    </div>
  );
}

function FInput({ value, onChange, placeholder, type='text' }) {
  return <input type={type} value={value||''} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
    style={{ width:'100%', padding:'8px 10px', border:`1px solid ${C.border}`, borderRadius:7, fontSize:13, fontFamily:"'DM Sans',sans-serif", outline:'none', boxSizing:'border-box' }}/>;
}

function FSelect({ value, onChange, options }) {
  return (
    <select value={value||''} onChange={e=>onChange(e.target.value)}
      style={{ width:'100%', padding:'8px 10px', border:`1px solid ${C.border}`, borderRadius:7, fontSize:13, fontFamily:"'DM Sans',sans-serif", outline:'none' }}>
      <option value="">Seleccionar...</option>
      {options.map(o=><option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function FMulti({ value=[], onChange, options }) {
  const toggle = opt => {
    const arr = value.includes(opt) ? value.filter(v=>v!==opt) : [...value, opt];
    onChange(arr);
  };
  return (
    <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
      {options.map(opt=>(
        <button key={opt} type="button" onClick={()=>toggle(opt)}
          style={{ background:value.includes(opt)?C.purpleLight:"transparent", border:`1px solid ${value.includes(opt)?C.purple:C.border}`, borderRadius:6, color:value.includes(opt)?C.purple:C.textMid, padding:'4px 10px', fontSize:12, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", fontWeight:value.includes(opt)?700:400 }}>
          {opt}
        </button>
      ))}
    </div>
  );
}

const SECTIONS = [
  "1. Informação sobre a empresa",
  "2. Âmbito e condições do contrato",
  "3. Dados dos colaboradores",
  "4. Setup técnico e integrações",
  "5. Comunicação interna e activação",
  "6. Dashboards, reporting e dados",
  "7. Gestão de caso e acompanhamento",
];

export default function BriefingModal({ projectId, projectName, onClose }) {
  const [briefing, setBriefing] = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [section,  setSection]  = useState(0);
  const [saved,    setSaved]    = useState(false);

  // Plano de sessões do projeto — só leitura aqui; edita-se no projeto.
  const [bolsas, setBolsas] = useState([]);
  const [refs, setRefs] = useState({});        // custo de referência por valência

  useEffect(() => { (async () => {
    const { data } = await supabase.from('valencias_custo_referencia').select('*');
    const m = {}; (data||[]).forEach(r => { m[r.valencia] = r; }); setRefs(m);
  })(); }, []);

  // O nome da bolsa é composto a partir do tipo e das valências — ninguém o escreve.
  const nomeBolsa = (b) => {
    const vs = (b.valencias||[]).map(v=>v.valencia);
    if (!vs.length) return b.tipo === 'pacote' ? 'Pacote' : '(sem valências)';
    if (vs.length === 1) return vs[0];
    if (b.tipo === 'pacote') return 'Pacote: ' + vs.join(', ');
    return 'À escolha: ' + vs.join(', ');
  };
  useEffect(() => {
    if(!projectId) { setBolsas([]); return; }
    (async () => {
      const { data: bs } = await supabase.from('projeto_bolsas')
        .select('id,nome,sessoes_ano,total_pacote,ordem').eq('projeto_id', projectId).order('ordem');
      if(!bs || !bs.length) { setBolsas([]); return; }
      const { data: vs } = await supabase.from('projeto_bolsa_valencias')
        .select('bolsa_id,valencia').in('bolsa_id', bs.map(b=>b.id));
      setBolsas(bs.map(b => ({ nome:b.nome, tipo:b.tipo||'dedicada', sessoes_ano:b.sessoes_ano ?? '', total_pacote:b.total_pacote ?? '',
        valencias: (vs||[]).filter(v=>v.bolsa_id===b.id).map(v=>({ valencia:v.valencia, custo_alvo: v.custo_alvo ?? '' })) })));
    })();
  }, [projectId]);

  useEffect(() => { loadBriefing(); }, [projectId]);

  const loadBriefing = async () => {
    setLoading(true);
    const { data } = await supabase.from('project_briefings').select('*').eq('project_id', projectId).single();
    if (data) {
      // Parse JSONB fields
      setBriefing({
        ...data,
        channels: data.channels || [],
        valencias: data.valencias || [],
        priority_metrics: data.priority_metrics || [],
        completed_sections: data.completed_sections || [],
      });
    } else {
      setBriefing({
        project_id: projectId,
        channels: [], valencias: [], priority_metrics: [], completed_sections: [],
        status: 'rascunho',
      });
    }
    setLoading(false);
  };

  const f = (k, v) => setBriefing(b => ({ ...b, [k]: v }));

  const saveBriefing = async () => {
    setSaving(true);
    try {
      const data = {
        project_id: projectId,
        ...briefing,
        channels: briefing.channels || [],
        valencias: briefing.valencias || [],
        priority_metrics: briefing.priority_metrics || [],
        completed_sections: briefing.completed_sections || [],
        updated_at: new Date().toISOString(),
      };
      delete data.id;
      delete data.created_at;

      // O plano de sessões vive em tabelas próprias. Regrava-se o conjunto —
      // são poucas linhas e evita comparar o que mudou.
      if (projectId) {
        await supabase.from('projeto_bolsas').delete().eq('projeto_id', projectId);
        for (const [i, b] of (bolsas||[]).entries()) {
          if (!(b.valencias||[]).length && !b.total_pacote) continue;   // bolsa vazia
          const { data: nova, error: eB } = await supabase.from('projeto_bolsas').insert({
            projeto_id: projectId, nome: nomeBolsa(b), tipo: b.tipo||'dedicada', ordem: i,
            sessoes_ano:  (b.sessoes_ano===''||b.sessoes_ano==null)?null:Number(b.sessoes_ano),
            total_pacote: (b.total_pacote===''||b.total_pacote==null)?null:Number(b.total_pacote),
          }).select('id').single();
          if (eB || !nova) { alert('Não consegui gravar a bolsa "'+nomeBolsa(b)+'": '+(eB?.message||'')); continue; }
          const vals = (b.valencias||[]).filter(v=>v.valencia).map(v=>({
            bolsa_id: nova.id, valencia: v.valencia,
            custo_alvo: (v.custo_alvo===''||v.custo_alvo==null)?null:Number(v.custo_alvo),
          }));
          if (vals.length) await supabase.from('projeto_bolsa_valencias').insert(vals);
        }
      }

      if (briefing.id) {
        const { error } = await supabase.from('project_briefings').update(data).eq('id', briefing.id);
        if (error) throw error;
      } else {
        const { data: inserted, error } = await supabase.from('project_briefings').insert({...data, project_id: projectId}).select().single();
        if (error) throw error;
        if (inserted) setBriefing(b => ({...b, id: inserted.id}));
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('Erro ao guardar briefing:', err);
      alert('Erro ao guardar: ' + (err.message || JSON.stringify(err)));
    }
    setSaving(false);
  };

  const signBriefing = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    f('status', 'assinado');
    f('signed_at', new Date().toISOString());
    f('signed_by', session?.user?.email);
    await saveBriefing();
  };

  const progress = briefing ? Math.round(((briefing.completed_sections||[]).length / 7) * 100) : 0;
  if (loading) return (
    <div style={{ position:'fixed', inset:0, background:'rgba(26,26,46,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
      <div style={{ background:C.white, borderRadius:12, padding:32, color:C.purple, fontFamily:"'DM Sans',sans-serif" }}>A carregar briefing...</div>
    </div>
  );

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(26,26,46,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, backdropFilter:'blur(4px)' }} onClick={onClose}>
      <div style={{ background:C.white, borderRadius:16, width:720, maxHeight:'90vh', display:'flex', flexDirection:'column', overflow:'hidden', boxShadow:'0 20px 56px rgba(0,0,0,0.22)', fontFamily:"'DM Sans',sans-serif" }} onClick={e=>e.stopPropagation()}>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Fraunces:wght@700&display=swap" rel="stylesheet"/>

        {/* HEADER */}
        <div style={{ background:C.purple, padding:'16px 24px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <h2 style={{ margin:0, fontFamily:"'Fraunces',serif", color:'white', fontSize:18 }}>📋 Briefing de Lançamento WOW</h2>
            <p style={{ margin:'3px 0 0', fontSize:12, color:'rgba(255,255,255,0.75)' }}>{projectName} · 7 secções · 36 checkpoints</p>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            {briefing?.status==='assinado'&&<span style={{ background:C.green, color:'white', borderRadius:6, padding:'4px 12px', fontSize:12, fontWeight:700 }}>✓ Assinado</span>}
            {briefing?.status==='rascunho'&&<span style={{ background:C.amberLight, color:C.amber, borderRadius:6, padding:'4px 12px', fontSize:12, fontWeight:700 }}>Rascunho</span>}
            <button onClick={onClose} style={{ background:'none', border:'none', color:'rgba(255,255,255,0.7)', fontSize:22, cursor:'pointer' }}>×</button>
          </div>
        </div>

        {/* PROGRESS */}
        <div style={{ padding:'10px 24px', background:C.purpleLight, borderBottom:`1px solid ${C.border}` }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ flex:1, height:6, background:C.border, borderRadius:3, overflow:'hidden' }}>
              <div style={{ width:`${progress}%`, height:'100%', background:C.purple, borderRadius:3, transition:'width 0.3s' }}/>
            </div>
            <span style={{ fontSize:12, color:C.purple, fontWeight:700, whiteSpace:'nowrap' }}>{progress}% completo ({(briefing?.completed_sections||[]).length}/7 secções)</span>
          </div>
          <div style={{ display:'flex', gap:6, marginTop:8, flexWrap:'wrap' }}>
            {SECTIONS.map((s,i)=>{
              const done=(briefing?.completed_sections||[]).includes(i);
              return(
                <button key={i} onClick={()=>setSection(i)} style={{ background:section===i?C.purple:done?C.greenLight:"white", border:`1px solid ${section===i?C.purple:done?C.green:C.border}`, borderRadius:5, color:section===i?'white':done?C.green:C.textLight, padding:'3px 10px', fontSize:10, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", fontWeight:section===i||done?700:400 }}>
                  {done?'✓ ':''}{s.split('. ')[1]||s}
                </button>
              );
            })}
          </div>
        </div>

        {/* CONTENT */}
        <div style={{ flex:1, overflowY:'auto', padding:'20px 24px' }}>

          {/* SECÇÃO 1 */}
          {section===0&&(
            <div>
              <h3 style={{ margin:'0 0 16px', color:C.purple, fontFamily:"'Fraunces',serif", fontSize:16 }}>1. Informação sobre a empresa</h3>
              <Field label="Nome completo e sector de atividade" tag="Onboarding">
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                  <FInput value={briefing?.company_name} onChange={v=>f('company_name',v)} placeholder="Nome da empresa"/>
                  <FInput value={briefing?.sector} onChange={v=>f('sector',v)} placeholder="Sector de atividade"/>
                </div>
              </Field>
              <Field label="Número de colaboradores e estrutura" tag="Onboarding">
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                  <FInput value={briefing?.num_employees} onChange={v=>f('num_employees',v)} placeholder="Nº total de colaboradores" type="number"/>
                  <FSelect value={briefing?.structure} onChange={v=>f('structure',v)} options={["Escritório","Híbrido","Remoto","Operações","Misto"]}/>
                </div>
              </Field>
              <Field label="Países e localizações abrangidas" tag="Onboarding">
                <FInput value={briefing?.locations} onChange={v=>f('locations',v)} placeholder="Ex: Portugal (Lisboa, Porto), Espanha (Madrid)"/>
              </Field>
              <Field label="Ponto focal WOW interno (SPOC)" tag="RH" note="Quem receberá dashboards, relatórios e comunicações da Workwell">
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8 }}>
                  <FInput value={briefing?.spoc_name} onChange={v=>f('spoc_name',v)} placeholder="Nome"/>
                  <FInput value={briefing?.spoc_email} onChange={v=>f('spoc_email',v)} placeholder="Email"/>
                  <FInput value={briefing?.spoc_phone} onChange={v=>f('spoc_phone',v)} placeholder="Telefone"/>
                </div>
              </Field>
              <CheckItem label="Existe acordo de confidencialidade (NDA) a formalizar previamente?" tag="Onboarding" value={briefing?.has_nda} onChange={v=>f('has_nda',v)}/>
            </div>
          )}

          {/* SECÇÃO 2 */}
          {section===1&&(
            <div>
              <h3 style={{ margin:'0 0 16px', color:C.purple, fontFamily:"'Fraunces',serif", fontSize:16 }}>2. Âmbito e condições do contrato</h3>
              <Field label="Pacote contratado" tag="Onboarding">
                <FSelect value={briefing?.package} onChange={v=>f('package',v)} options={PACKAGES}/>
              </Field>
              {/* Plano de sessões. Uma bolsa é um conjunto de sessões que o
                  colaborador pode gastar: com uma valência são dedicadas, com
                  várias escolhe onde as usa. O custo-alvo é interno. */}
              <Field label="Plano de sessões" tag="Onboarding">
                <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:8 }}>
                  <button type="button" onClick={()=>setBolsas([...(bolsas||[]),{ tipo:'dedicada', sessoes_ano:'', total_pacote:'', valencias:[] }])}
                    style={{ background:'#EDE7F6', border:'none', color:C.purple, borderRadius:7, padding:'6px 12px', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" }}>
                    + Bolsa de sessões</button>
                </div>

                {(bolsas||[]).length===0 && (
                  <div style={{ fontSize:12.5, color:C.soft, padding:'8px 0' }}>
                    Ainda sem plano definido. Acrescenta uma bolsa com as sessões contratadas.
                  </div>
                )}

                {(bolsas||[]).map((b,bi)=>(
                  <div key={bi} style={{ border:'1px solid '+C.border, borderRadius:8, padding:'10px 12px', marginBottom:8, background:'#FAFAFF' }}>
                    <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'flex-end', marginBottom:8 }}>
                      <div style={{ flex:2, minWidth:145 }}>
                        <label style={{ fontSize:10.5, fontWeight:600, color:C.soft, display:'block', marginBottom:3 }}>Tipo</label>
                        <select value={b.tipo||'dedicada'}
                          onChange={e=>setBolsas(bolsas.map((x,k)=>k===bi?{...x,tipo:e.target.value}:x))}
                          style={{ width:'100%', padding:'7px 9px', border:'1px solid '+C.border, borderRadius:6, fontSize:12.5, fontFamily:"'DM Sans',sans-serif", boxSizing:'border-box', background:'#fff' }}>
                          <option value="dedicada">Dedicada a uma valência</option>
                          <option value="escolha">À escolha entre valências</option>
                          <option value="pacote">Pacote de consultas</option>
                        </select>
                      </div>
                      <div style={{ flex:1, minWidth:105 }}>
                        <label style={{ fontSize:10.5, fontWeight:600, color:C.soft, display:'block', marginBottom:3 }}>Sessões/pessoa/ano</label>
                        <input type="number" value={b.sessoes_ano ?? ''} placeholder="ex.: 6"
                          onChange={e=>setBolsas(bolsas.map((x,k)=>k===bi?{...x,sessoes_ano:e.target.value}:x))}
                          style={{ width:'100%', padding:'7px 9px', border:'1px solid '+C.border, borderRadius:6, fontSize:12.5, fontFamily:"'DM Sans',sans-serif", boxSizing:'border-box' }}/>
                      </div>
                      <div style={{ flex:1, minWidth:105 }}>
                        <label style={{ fontSize:10.5, fontWeight:600, color:C.soft, display:'block', marginBottom:3 }}>Ou total do pacote</label>
                        <input type="number" value={b.total_pacote ?? ''} placeholder="ex.: 140"
                          onChange={e=>setBolsas(bolsas.map((x,k)=>k===bi?{...x,total_pacote:e.target.value}:x))}
                          style={{ width:'100%', padding:'7px 9px', border:'1px solid '+C.border, borderRadius:6, fontSize:12.5, fontFamily:"'DM Sans',sans-serif", boxSizing:'border-box' }}/>
                      </div>
                      <button type="button" onClick={()=>setBolsas(bolsas.filter((_,k)=>k!==bi))}
                        style={{ background:'#FDECEA', border:'none', color:'#C0392B', borderRadius:6, padding:'7px 10px', fontSize:11.5, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" }}>Remover</button>
                    </div>

                    <div style={{ fontSize:12.5, fontWeight:700, color:C.purple, marginBottom:7 }}>{nomeBolsa(b)}</div>
                    <label style={{ fontSize:10.5, fontWeight:600, color:C.soft, display:'block', marginBottom:5 }}>Valências que esta bolsa cobre</label>
                    <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                      {VALENCIAS.map(v=>{
                        const sel=(b.valencias||[]).find(x=>x.valencia===v);
                        return (
                          <button key={v} type="button"
                            onClick={()=>setBolsas(bolsas.map((x,k)=>k===bi?{...x,
                              valencias: sel ? (x.valencias||[]).filter(y=>y.valencia!==v)
                                             : [...(x.valencias||[]),{ valencia:v, custo_alvo: refs[v]?.custo_tipico ?? '' }]}:x))}
                            style={{ padding:'5px 10px', borderRadius:13, border:`1.5px solid ${sel?C.purple:C.border}`, background:sel?'#EDE7F6':'#fff', color:sel?C.purple:C.soft, fontSize:11.5, fontWeight:sel?700:400, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" }}>
                            {v}</button>
                        );
                      })}
                    </div>

                    {(b.valencias||[]).length>0 && (
                      <>
                        <div style={{ fontSize:10.5, color:C.soft, margin:'9px 0 5px', fontWeight:600 }}>
                          Custo-alvo por sessão <span style={{ fontWeight:400 }}>· interno, não é para partilhar</span>
                        </div>
                        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(165px,1fr))', gap:7 }}>
                          {(b.valencias||[]).map((v,vi)=>(
                            <div key={v.valencia}>
                              <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                                <span style={{ fontSize:11.5, color:C.txt, flex:1 }}>{v.valencia}</span>
                                <input type="number" step="0.01" value={v.custo_alvo ?? ''} placeholder="€"
                                  onChange={e=>setBolsas(bolsas.map((x,k)=>k===bi?{...x,
                                    valencias:(x.valencias||[]).map((y,z)=>z===vi?{...y,custo_alvo:e.target.value}:y)}:x))}
                                  style={{ width:76, padding:'5px 8px', border:'1px solid '+C.border, borderRadius:6, fontSize:12, fontFamily:"'DM Sans',sans-serif" }}/>
                              </div>
                              {refs[v.valencia] && (() => {
                                const r = refs[v.valencia], val = Number(v.custo_alvo);
                                const foraDoIntervalo = val>0 && r.minimo!=null && r.maximo!=null && (val < r.minimo || val > r.maximo);
                                return (
                                  <div style={{ fontSize:10, color: foraDoIntervalo ? '#B9770E' : C.soft, marginTop:2, lineHeight:1.4 }}>
                                    {foraDoIntervalo
                                      ? `fora do praticado (${r.minimo}–${r.maximo}€) — confirma`
                                      : `praticado: ${r.custo_tipico}€${r.minimo!=null && r.minimo!==r.maximo ? ` (${r.minimo}–${r.maximo}€)` : ''}`}
                                  </div>
                                );
                              })()}
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </Field>

              <Field label="Adesão prevista (%)" tag="Onboarding">
                <FInput value={briefing?.adesao_prevista_pct} onChange={v=>f('adesao_prevista_pct',v)} type="number" placeholder="ex.: 1"/>
              </Field>
              <CheckItem label="Cláusula de renovação automática ou prazo de aviso prévio definido?" tag="Onboarding" value={briefing?.auto_renewal} onChange={v=>f('auto_renewal',v)}/>
            </div>
          )}

          {/* SECÇÃO 3 */}
          {section===2&&(
            <div>
              <h3 style={{ margin:'0 0 16px', color:C.purple, fontFamily:"'Fraunces',serif", fontSize:16 }}>3. Dados dos colaboradores</h3>
              <Field label="Listagem de colaboradores elegíveis" tag="Técnico" note="Formato preferencial: Excel ou CSV. Data de nascimento opcional.">
                <FSelect value={briefing?.employees_list_format} onChange={v=>f('employees_list_format',v)} options={["Excel (.xlsx)","CSV","Google Sheets","Envio directo por email","Outro"]}/>
              </Field>
              <CheckItem label="A plataforma estende-se a familiares ou dependentes?" tag="Onboarding" value={briefing?.extends_to_family} onChange={v=>f('extends_to_family',v)}/>
              {briefing?.extends_to_family&&(
                <Field label="Quantos dependentes por colaborador?" tag="Onboarding">
                  <FInput value={briefing?.family_per_employee} onChange={v=>f('family_per_employee',v)} placeholder="Ex: até 2 dependentes por colaborador"/>
                </Field>
              )}
              <CheckItem label="Existem grupos de acesso diferenciados (ex: managers vs. colaboradores)?" tag="RH" value={briefing?.has_access_groups} onChange={v=>f('has_access_groups',v)}/>
              <Field label="Língua preferencial da plataforma" tag="Onboarding">
                <FSelect value={briefing?.platform_language} onChange={v=>f('platform_language',v)} options={["Português","Inglês","Espanhol","Francês","Bilingue PT/EN"]}/>
              </Field>
              <CheckItem label="Os colaboradores têm email corporativo acessível?" tag="Técnico" value={briefing?.has_corporate_email} onChange={v=>f('has_corporate_email',v)}/>
            </div>
          )}

          {/* SECÇÃO 4 */}
          {section===3&&(
            <div>
              <h3 style={{ margin:'0 0 16px', color:C.purple, fontFamily:"'Fraunces',serif", fontSize:16 }}>4. Setup técnico e integrações</h3>
              <CheckItem label="SSO necessário?" tag="Técnico" value={briefing?.needs_sso} onChange={v=>f('needs_sso',v)}/>
              {briefing?.needs_sso&&(
                <Field label="Fornecedor de identidade SSO" tag="Técnico">
                  <FSelect value={briefing?.sso_provider} onChange={v=>f('sso_provider',v)} options={["Azure AD","Okta","Google Workspace","Outro"]}/>
                </Field>
              )}
              <Field label="Integração com HRIS existente" tag="Técnico">
                <FSelect value={briefing?.hris_integration} onChange={v=>f('hris_integration',v)} options={["Nenhum","SAP","Workday","BambooHR","Personio","Factorial","Outro"]}/>
              </Field>
              <CheckItem label="A política de IT permite instalação de app em dispositivos pessoais?" tag="Técnico" value={briefing?.app_on_personal_devices} onChange={v=>f('app_on_personal_devices',v)}/>
              <CheckItem label="Restrições de firewall? Necessidade de whitelist?" tag="Técnico" value={briefing?.firewall_restrictions} onChange={v=>f('firewall_restrictions',v)}/>
              <Field label="Contacto do responsável técnico de IT" tag="Técnico">
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                  <FInput value={briefing?.it_contact_name} onChange={v=>f('it_contact_name',v)} placeholder="Nome"/>
                  <FInput value={briefing?.it_contact_email} onChange={v=>f('it_contact_email',v)} placeholder="Email"/>
                </div>
              </Field>
            </div>
          )}

          {/* SECÇÃO 5 */}
          {section===4&&(
            <div>
              <h3 style={{ margin:'0 0 16px', color:C.purple, fontFamily:"'Fraunces',serif", fontSize:16 }}>5. Comunicação interna e activação</h3>
              <Field label="Canal principal de comunicação interna" tag="Comunicação">
                <FMulti value={briefing?.channels||[]} onChange={v=>f('channels',v)} options={CHANNELS}/>
              </Field>
              <CheckItem label="Existe equipa de comunicação interna?" tag="Comunicação" value={briefing?.has_comms_team} onChange={v=>f('has_comms_team',v)}/>
              {briefing?.has_comms_team&&(
                <Field label="Quem valida e aprova mensagens?" tag="Comunicação">
                  <FInput value={briefing?.comms_approver} onChange={v=>f('comms_approver',v)} placeholder="Nome/cargo do aprovador"/>
                </Field>
              )}
              <Field label="Data pretendida para lançamento interno" tag="Comunicação">
                <FInput value={briefing?.preferred_launch_date} onChange={v=>f('preferred_launch_date',v)} type="date"/>
              </Field>
              <Field label="Tom da comunicação interna" tag="Comunicação">
                <FSelect value={briefing?.comms_tone} onChange={v=>f('comms_tone',v)} options={TONES}/>
              </Field>
              <CheckItem label="A empresa pretende sessão de sensibilização no lançamento?" tag="Comunicação" value={briefing?.wants_sensitization_session} onChange={v=>f('wants_sensitization_session',v)}/>
              <Field label="Datas a evitar para o lançamento" tag="Comunicação">
                <FInput value={briefing?.dates_to_avoid} onChange={v=>f('dates_to_avoid',v)} placeholder="Ex: Agosto (férias), Dezembro (balanço anual)"/>
              </Field>
            </div>
          )}

          {/* SECÇÃO 6 */}
          {section===5&&(
            <div>
              <h3 style={{ margin:'0 0 16px', color:C.purple, fontFamily:"'Fraunces',serif", fontSize:16 }}>6. Dashboards, reporting e proteção de dados</h3>
              <Field label="Quem acede ao dashboard de RH?" tag="RH">
                <FInput value={briefing?.dashboard_access} onChange={v=>f('dashboard_access',v)} placeholder="Ex: Directora RH + 2 gestores de pessoas"/>
              </Field>
              <Field label="Frequência de relatórios pretendida" tag="RH">
                <FSelect value={briefing?.reporting_frequency} onChange={v=>f('reporting_frequency',v)} options={FREQUENCIES}/>
              </Field>
              <Field label="Métricas prioritárias" tag="RH">
                <FMulti value={briefing?.priority_metrics||[]} onChange={v=>f('priority_metrics',v)} options={METRICS}/>
              </Field>
              <CheckItem label="Existe DPO ou CISO que precise de validar o tratamento de dados?" tag="RH" note="Necessário para assinar DPA / cumprimento RGPD" value={briefing?.has_dpo} onChange={v=>f('has_dpo',v)}/>
              {briefing?.has_dpo&&(
                <Field label="Contacto do DPO" tag="RH">
                  <FInput value={briefing?.dpo_contact} onChange={v=>f('dpo_contact',v)} placeholder="Nome e email do DPO"/>
                </Field>
              )}
              <CheckItem label="A empresa pretende alertas automáticos em caso de risco elevado?" tag="RH" value={briefing?.wants_risk_alerts} onChange={v=>f('wants_risk_alerts',v)}/>
            </div>
          )}

          {/* SECÇÃO 7 */}
          {section===6&&(
            <div>
              <h3 style={{ margin:'0 0 16px', color:C.purple, fontFamily:"'Fraunces',serif", fontSize:16 }}>7. Gestão de caso e acompanhamento</h3>
              <CheckItem label="A empresa pretende reuniões periódicas com a Workwell?" tag="RH" value={briefing?.wants_periodic_meetings} onChange={v=>f('wants_periodic_meetings',v)}/>
              {briefing?.wants_periodic_meetings&&(
                <Field label="Com que frequência?" tag="RH">
                  <FSelect value={briefing?.meeting_frequency} onChange={v=>f('meeting_frequency',v)} options={FREQUENCIES}/>
                </Field>
              )}
              <Field label="Existe programa de bem-estar já em curso?" tag="Onboarding">
                <FInput value={briefing?.existing_wellbeing_program} onChange={v=>f('existing_wellbeing_program',v)} placeholder="Ex: Seguro saúde com psicologia, EAP anterior..."/>
              </Field>
              <Field label="Preocupações específicas identificadas" tag="RH" note="Permite priorizar valências, conteúdos e alertas no setup inicial">
                <textarea value={briefing?.specific_concerns||''} onChange={e=>f('specific_concerns',e.target.value)} rows={3} placeholder="Ex: Burnout elevado, absentismo, conflitos de equipa..."
                  style={{ width:'100%', padding:'8px 10px', border:`1px solid ${C.border}`, borderRadius:7, fontSize:13, fontFamily:"'DM Sans',sans-serif", outline:'none', boxSizing:'border-box', resize:'vertical' }}/>
              </Field>
              <Field label="OKRs ou KPIs de bem-estar que quer monitorizar via WOW" tag="RH">
                <FInput value={briefing?.wellbeing_okrs} onChange={v=>f('wellbeing_okrs',v)} placeholder="Ex: Reduzir absentismo 15%, NPS colaboradores ≥ 7"/>
              </Field>

              {/* LEGENDA TAGS */}
              <div style={{ marginTop:20, background:C.bg, borderRadius:9, padding:14 }}>
                <div style={{ fontSize:11, fontWeight:700, color:C.textLight, textTransform:'uppercase', letterSpacing:0.8, marginBottom:8 }}>Legenda de responsabilidades</div>
                <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                  {Object.entries(TAGS).map(([k,v])=>(
                    <span key={k} style={{ background:v.bg, color:v.color, borderRadius:5, padding:'3px 10px', fontSize:11, fontWeight:700 }}>{k}</span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div style={{ padding:'14px 24px', borderTop:`1px solid ${C.border}`, display:'flex', gap:10, alignItems:'center' }}>
          <button onClick={()=>setSection(s=>Math.max(0,s-1))} disabled={section===0}
            style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:7, color:section===0?C.textLight:C.purple, padding:'8px 16px', fontSize:13, cursor:section===0?'not-allowed':'pointer', fontFamily:"'DM Sans',sans-serif" }}>
            ‹ Anterior
          </button>
          <button onClick={()=>{
            const done=[...(briefing?.completed_sections||[])];
            if(!done.includes(section))done.push(section);
            f('completed_sections',done);
            if(section<6)setSection(s=>s+1);
          }} style={{ background:C.purpleLight, border:`1px solid ${C.purple}`, borderRadius:7, color:C.purple, padding:'8px 16px', fontSize:13, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", fontWeight:600 }}>
            ✓ Marcar secção completa {section<6?'→ Próxima':''}
          </button>
          <div style={{ flex:1 }}/>
          {saved&&<span style={{ fontSize:12, color:C.green, fontWeight:600 }}>✓ Guardado!</span>}
          <button onClick={saveBriefing} disabled={saving}
            style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:7, color:C.textMid, padding:'8px 16px', fontSize:13, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" }}>
            {saving?'A guardar...':'💾 Guardar'}
          </button>
          {briefing?.status!=='assinado'&&(
            <button onClick={signBriefing}
              style={{ background:C.purple, border:'none', borderRadius:7, color:'white', padding:'8px 16px', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" }}>
              ✍️ Assinar Briefing
            </button>
          )}
        </div>
      </div>
    </div>
  );
}